/**
 * The Aura preset the app installs — the one place a brand colour, severity tint, chart
 * hue, heatmap step or band hue is decided. Everything else reads `--p-*`. Literals and
 * recipes live in theme-palette.ts; this file only composes them into Aura's token tree.
 */
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import {
  BANNER,
  BAND_COLORS,
  BUTTON_FAMILY,
  CATEGORICAL,
  CHART_SERIES,
  CHECKBOX_EDGE,
  FIELD_EDGE_DARK,
  HEATMAP,
  HIGHLIGHT,
  MUTED_LIGHT,
  NAV,
  PRIMARY_RAMP,
  SELECTED_ROW_DARK,
  SEVERITY_FAMILY,
  SURFACE_DARK,
  SURFACE_LIGHT,
  TOOLBAR,
  severityButton,
  severityOutlined,
  severityText,
  tint,
  tokenRef,
  type ButtonSeverity,
  type Family,
  type Scheme,
  type Severity,
  type SeverityFamily,
} from './theme-palette';

const MUTED = '{text.muted.color}';

const bySeverity = <T>(recipe: (family: SeverityFamily) => T): Record<Severity, T> => ({
  success: recipe(SEVERITY_FAMILY.success),
  info: recipe(SEVERITY_FAMILY.info),
  warn: recipe(SEVERITY_FAMILY.warn),
  danger: recipe(SEVERITY_FAMILY.danger),
});

const byButtonSeverity = <T>(recipe: (family: Family) => T): Record<ButtonSeverity, T> => ({
  ...bySeverity(recipe),
  help: recipe(BUTTON_FAMILY.help),
});

/** Message and toast spell the fourth severity `error`, not `danger`. */
const asMessageKeys = <T>({ success, info, warn, danger }: Record<Severity, T>) => ({
  success,
  info,
  warn,
  error: danger,
});

const mutedIcons = {
  navigation: { item: { icon: { color: MUTED } } },
  list: { option: { icon: { color: MUTED } } },
};

const closeButton = (f: SeverityFamily) => ({
  hoverBackground: tokenRef(f, 200),
  focusRing: { color: tokenRef(f, 700) },
});

/** Toasts keep the tint recipe (100 / 200 / 700; dark 950 / 700 / 300). */
const toastTones = {
  light: asMessageKeys(bySeverity((f) => ({ ...tint(f, 'light'), closeButton: closeButton(f) }))),
  dark: asMessageKeys(bySeverity((f) => tint(f, 'dark'))),
};

/** Banners wear prod's tones, outlined in their own ink. */
const banner = (f: SeverityFamily, scheme: Scheme) => {
  const { background, color } = BANNER[scheme][f];
  return { background, color, borderColor: color };
};

const messageTones = {
  light: asMessageKeys(bySeverity((f) => ({ ...banner(f, 'light'), closeButton: closeButton(f) }))),
  dark: asMessageKeys(bySeverity((f) => banner(f, 'dark'))),
};

const mutedSpinButtons = { button: { color: MUTED } };

export const CustomAuraPreset = definePreset(Aura, {
  semantic: {
    primary: PRIMARY_RAMP,
    colorScheme: {
      light: {
        primary: {
          color: '{primary.600}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.700}',
          activeColor: '{primary.800}',
        },
        highlight: HIGHLIGHT.light,
        surface: SURFACE_LIGHT,
        text: { mutedColor: MUTED_LIGHT },
        formField: {
          // Prod's field: white with the light card edge (a 1.3:1 boundary; prod parity is the rule).
          background: '{surface.0}',
          borderColor: '{surface.300}',
          // Develop's hover edge, kept by call (palette row 20).
          hoverBorderColor: MUTED_LIGHT,
          invalidBorderColor: '{red.600}',
          placeholderColor: MUTED,
          // Prod's (Aura's) field icon: the select chevron and the calendar glyph.
          iconColor: '{surface.400}',
          floatLabelColor: MUTED,
          color: '{text.color}',
          shadow: 'none',
        },
        ...mutedIcons,
      },
      dark: {
        // Prod's dark primary: the ramp's 500 on its 900, lightening through 200 / 100.
        primary: {
          color: '{primary.500}',
          contrastColor: '{primary.900}',
          hoverColor: '{primary.200}',
          activeColor: '{primary.100}',
        },
        highlight: HIGHLIGHT.dark,
        surface: SURFACE_DARK,
        // Prod's dark muted ink, zinc.400; the ramp's 400 is slate.
        text: { mutedColor: '{zinc.400}' },
        formField: {
          // Prod's dark field, #18181b on #667085, focused in the primary (palette row 22); the
          // hover edge stays develop's zinc.400 by call (palette row 20).
          background: '{surface.950}',
          borderColor: FIELD_EDGE_DARK,
          hoverBorderColor: '{zinc.400}',
          placeholderColor: MUTED,
          // Prod's (Aura's) field icon: the select chevron and the calendar glyph.
          iconColor: '{surface.400}',
          floatLabelColor: MUTED,
          color: '{text.color}',
          shadow: 'none',
        },
        ...mutedIcons,
      },
    },
    // ── Compact density standard ──────────────────────────────────
    // Density is a THEME-LEVEL concept (Material density scale / Carbon row
    // sizes): it compresses spacing and heights only — never font size, never
    // horizontal breathing room.
    //
    // Every rem value is scaled × 0.875 (14/16) so the 16px root keeps the compact px —
    // controls 31.875px, body rows 28.25px, header rows 31.75px, paginator 35px.
    // Controls render on the sm tier (14px text), so sm carries the default tier's
    // paddingY and a small control keeps the 31.875px height.
    formField: {
      paddingY: '0.41015625rem',
      sm: { paddingY: '0.41015625rem', paddingX: '0.65625rem' },
    },
    // Dropdown/list options: compact rows matching the table body rhythm.
    // Also drives datatable filter overlays (their tokens reference list.*).
    list: { option: { padding: '0.328125rem 0.65625rem' } },
  },

  // Component-level density tokens. Overriding the DEFAULT tier makes
  // every instance compact with zero template edits; the sm tiers the app
  // renders are scaled alongside so they keep their compact px.
  components: {
    datatable: {
      // Body 0.328125rem (5.25px) vertical → 28.25px rows (compact BI density);
      // header 0.4375rem (7px) → 31.75px. Horizontal
      // stays generous at 0.765625rem: dense tables compress vertically, never
      // horizontally. The sm tier keeps Aura's tighter sm padding at the same px.
      headerCell: { padding: '0.4375rem 0.765625rem', sm: { padding: '0.328125rem 0.4375rem' } },
      bodyCell: { padding: '0.328125rem 0.765625rem', sm: { padding: '0.328125rem 0.4375rem' } },
      footerCell: { padding: '0.328125rem 0.765625rem', sm: { padding: '0.328125rem 0.4375rem' } },
      header: { padding: '0.4375rem 0.765625rem', sm: { padding: '0.328125rem 0.4375rem' } },
      footer: { padding: '0.328125rem 0.765625rem', sm: { padding: '0.328125rem 0.4375rem' } },
      colorScheme: { dark: { row: { selectedBackground: SELECTED_ROW_DARK } } },
    },
    paginator: {
      // Slim bar under dense tables; 1.75rem (28px) nav buttons stay above the
      // 24px hit-target floor.
      root: { padding: '0.21875rem 0.4375rem' },
      // The current page keeps Aura's selection highlight, the tint prod shows.
      navButton: { width: '1.75rem', height: '1.75rem' },
    },
    card: {
      root: { shadow: 'none' },
      // 0.875rem = 14px inset — cards read as frames, not pillows.
      body: { padding: '0.875rem' },
      // Card-title typography aligned with the house heading standard
      // (text-base font-semibold). Without this, Aura's 1.25rem/500 title
      // font CASCADES into everything projected beside the title — the
      // [cardActions]/[cardChip] slots — inflating dropdown/chip text to
      // 20px at the 16px root (the pricing "Group By" bug class).
      title: { fontSize: '1rem', fontWeight: '600' },
    },
    message: {
      content: { padding: '0.546875rem 0.765625rem', sm: { padding: '0.328125rem 0.546875rem' } },
      text: { fontWeight: '500', fontSize: '0.875rem' },
      colorScheme: messageTones,
    },
    toast: { colorScheme: toastTones },
    // Tags keep Aura's own tones, as prod does: sky info, 500 washes in dark.
    tag: { root: { fontWeight: '600', fontSize: '0.75rem' }, icon: { size: '1em' } },
    badge: {
      root: { fontWeight: '600' },
      colorScheme: {
        light: bySeverity((f) => ({ background: tokenRef(f, 700), color: '{surface.0}' })),
        dark: bySeverity((f) => ({ background: tokenRef(f, 400), color: tokenRef(f, 950) })),
      },
    },
    drawer: { title: { fontSize: '1.25rem' } },
    // Prod's tooltip: a white card with muted ink in light, the dark card with muted ink in dark.
    tooltip: {
      root: { borderRadius: '0.5rem', shadow: '0 4px 12px rgba(0, 0, 0, 0.1)' },
      colorScheme: {
        light: { root: { background: '{surface.0}', color: '{surface.500}' } },
        dark: { root: { background: '{surface.900}', color: MUTED } },
      },
    },
    // Prod's light skeleton is slate.200; dark keeps Aura's white wash.
    skeleton: { colorScheme: { light: { root: { background: '{slate.200}' } } } },
    // Prod's divider: slate.300 in light, the dark --surface-border grey in dark.
    divider: {
      colorScheme: {
        light: { root: { borderColor: '{slate.300}' } },
        dark: { root: { borderColor: '{zinc.700}' } },
      },
    },
    button: {
      colorScheme: {
        light: {
          root: byButtonSeverity((f) => severityButton(f, 'light')),
          outlined: byButtonSeverity((f) => severityOutlined(f, 'light')),
          text: {
            ...byButtonSeverity((f) => severityText(f, 'light')),
            secondary: { color: '{surface.700}' },
          },
        },
        dark: {
          root: byButtonSeverity((f) => severityButton(f, 'dark')),
          outlined: {
            ...byButtonSeverity((f) => severityOutlined(f, 'dark')),
            primary: { hoverBackground: 'transparent' },
          },
          text: {
            ...byButtonSeverity((f) => severityText(f, 'dark')),
            primary: { hoverBackground: 'transparent' },
            secondary: { color: MUTED },
          },
        },
      },
    },
    togglebutton: {
      // SelectButton renders each option as a togglebutton. Two Aura defaults are
      // corrected on its root, neither touching color/tint or height:
      //   • Focus ring: it sits 2px OUTSIDE the control (`focus.ring.offset`); on the
      //     pricing-action queue the pills tuck under an opaque sticky header that
      //     clips that top 2px on the selected pill. Inset it — offset 0, no
      //     projecting shadow — so it can't be clipped.
      //   • Horizontal padding: the root carries a 0.25rem gutter on ALL sides, but
      //     with no border at x=0 the first pill's fill starts inset, so the top
      //     label reads as if it overhangs left — unlike bordered controls whose
      //     border is flush at x=0. Zero ONLY the horizontal padding (keep the
      //     vertical 0.21875rem = 3.5px, mirrored across the sm/lg tiers the app
      //     renders) so the fill — and its label — flush to x=0. The default
      //     `content.padding` (0.25rem 0.75rem) is left untouched, so the pill TEXT
      //     keeps its inset, matching a select's; the sm tier sets its own content
      //     padding.
      root: {
        padding: '0.21875rem 0',
        sm: { padding: '0.21875rem 0' },
        lg: { padding: '0.21875rem 0' },
        focusRing: { offset: '0', shadow: 'none' },
      },
      content: { sm: { padding: '0.21875rem 0.65625rem' } },
    },
    inputnumber: { colorScheme: { light: mutedSpinButtons, dark: mutedSpinButtons } },
    // px on purpose: pins the 20px box whatever the root size. Prod checks on the ramp's 500
    // (hover 600) under a white tick in both schemes, inside its own edge greys.
    checkbox: {
      root: {
        width: '20px',
        height: '20px',
        checkedBackground: '{primary.500}',
        checkedBorderColor: '{primary.500}',
        checkedHoverBackground: '{primary.600}',
        checkedHoverBorderColor: '{primary.600}',
        checkedFocusBorderColor: '{primary.500}',
      },
      icon: { checkedColor: '{surface.0}', checkedHoverColor: '{surface.0}' },
      colorScheme: {
        light: {
          root: {
            borderColor: CHECKBOX_EDGE.light.rest,
            hoverBorderColor: CHECKBOX_EDGE.light.hover,
          },
        },
        dark: {
          root: {
            borderColor: CHECKBOX_EDGE.dark.rest,
            hoverBorderColor: CHECKBOX_EDGE.dark.hover,
          },
        },
      },
    },
    tabs: { tablist: { background: 'transparent' }, tabpanel: { background: 'transparent' } },
  },

  extend: {
    colorScheme: {
      light: {
        categorical: CATEGORICAL.light,
        heatmap: HEATMAP.light,
        band: BAND_COLORS.light,
        series: CHART_SERIES.light,
        nav: NAV.light,
        headerAction: TOOLBAR.light,
      },
      dark: {
        categorical: CATEGORICAL.dark,
        heatmap: HEATMAP.dark,
        band: BAND_COLORS.dark,
        series: CHART_SERIES.dark,
        nav: NAV.dark,
        headerAction: TOOLBAR.dark,
      },
    },
  },
});

/**
 * The theme configuration the application installs, as ONE value.
 *
 * `darkModeSelector` is the load-bearing part. Left unset, PrimeNG defaults to `'system'`
 * and emits every dark token inside `@media (prefers-color-scheme: dark)` instead of under
 * `.p-dark` — so the class `ThemeService` toggles selects nothing and dark mode is decided
 * by the machine rather than by the user. A test bed that omits it agrees with the app
 * wherever the machine happens to prefer dark and disagrees everywhere else, which is a
 * disagreement that only shows up on somebody else's computer.
 *
 * PrimeNG injects this stylesheet once per document, so the FIRST configuration to render
 * owns the page for the rest of the session — including for every later test. That is why
 * this is a shared constant rather than a literal each caller repeats: the app and the
 * specs are not merely similar here, they have to be the same object's worth of settings.
 */
export const CUSTOM_THEME = {
  preset: CustomAuraPreset,
  options: {
    prefix: 'p',
    darkModeSelector: '.p-dark',
    cssLayer: {
      name: 'primeng',
      order: 'tailwind-base, primeng, tailwind-utilities',
    },
  },
} as const;
